import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { notifier } from 'components/Toaster';
import { useQueryClient } from 'react-query';
import { AppButton } from 'components';
import useLessonIssueStatus from 'hooks/API/POST/useLessonIssueStatus';
import {
  DetailPageShell,
  DetailSection,
  DetailTable,
  MissingDetailState,
  formatDateTime,
} from 'pages/lesson-report-details/DetailLayout';

const STATUS_LABELS = {
  open: 'Open',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const ISSUE_TYPE_LABELS = {
  incorrect_content: 'Incorrect content',
  broken_content: 'Broken content',
  typo: 'Typo',
  missing_content: 'Missing content',
  other: 'Other',
};

function IssueDetail() {
  const { state } = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();

  const issue = state?.issue;

  const { mutate: updateStatus } = useLessonIssueStatus();

  if (!issue) {
    return (
      <MissingDetailState
        links={[{ label: 'Lesson Issues', to: '/manage/lesson-issues' }]}
        current="Issue Details"
        message="Issue details not found. Please go back and select an issue."
        onBack={() => history.push('/manage/lesson-issues')}
        backLabel="Back to Issues"
      />
    );
  }

  const handleStatus = (status) => {
    updateStatus(
      { issueId: issue._id, status },
      {
        onSuccess: () => {
          notifier.success(`Issue marked as ${status}`);
          queryClient.invalidateQueries('lesson-issues');
          // Optionally go back or just let user see it updated
          history.push('/manage/lesson-issues');
        },
        onError: () => notifier.error('Failed to update status'),
      }
    );
  };

  return (
    <DetailPageShell
      links={[{ label: 'Lesson Issues', to: '/manage/lesson-issues' }]}
      current="Issue Details"
      title={ISSUE_TYPE_LABELS[issue.issueType] || 'Lesson Issue'}
      status={STATUS_LABELS[issue.status]}
      statusClassName={`badge badge-${
        issue.status === 'open' ? 'warning' : issue.status === 'resolved' ? 'success' : 'secondary'
      }`}
      subtitle={`Reported on ${formatDateTime(issue.createdAt)}`}
      onBack={() => history.push('/manage/lesson-issues')}
      actions={
        issue.status === 'open' ? (
          <>
            <AppButton variantClass="btn btn-success" onClick={() => handleStatus('resolved')}>
              Resolve
            </AppButton>
            <AppButton variantClass="btn btn-secondary" onClick={() => handleStatus('dismissed')}>
              Dismiss
            </AppButton>
          </>
        ) : null
      }
    >
      <div className="row">
        <div className="col-lg-8">
          <DetailSection title="Issue Summary">
            <DetailTable
              rows={[
                { label: 'Student', value: issue.user?.name || issue.user?.email || '—' },
                { label: 'Course', value: issue.course?.courseName || '—' },
                { label: 'Chapter', value: issue.chapter?.name || '—' },
                { label: 'Lesson', value: issue.topic?.name || '—' },
                {
                  label: 'Issue Type',
                  value: ISSUE_TYPE_LABELS[issue.issueType] || issue.issueType || '—',
                },
                { label: 'Status', value: STATUS_LABELS[issue.status] || '—' },
                { label: 'Reported At', value: formatDateTime(issue.createdAt) },
              ]}
            />
          </DetailSection>
        </div>

        <div className="col-lg-4">
          <DetailSection title="Context">
            <DetailTable
              rows={[
                { label: 'Student Email', value: issue.user?.email || '—' },
                { label: 'Student Name', value: issue.user?.name || '—' },
                { label: 'Issue ID', value: issue._id || '—' },
              ]}
            />
          </DetailSection>
        </div>
      </div>

      <DetailSection title="Description">
        <DetailTable
          rows={[
            {
              label: 'Reported Text',
              value: issue.description || 'No additional description provided.',
              preserveWhitespace: true,
            },
          ]}
        />
      </DetailSection>
    </DetailPageShell>
  );
}

export default IssueDetail;
